// Promise-wrapped IndexedDB — the local-only store behind BYOK keys (PRD 006),
// session persistence (PRD 002 F7), and the contact book (docs/ideas.md).
// Deliberately tiny and dependency-free: key-value object stores, no indexes.
// `onupgradeneeded` creates any missing store, so adding one is a version bump
// plus a name here — v2 added 'contacts'. Everything in the browser, no server.

const DB_NAME = 'convariance'
const DB_VERSION = 2

export type StoreName = 'keys' | 'sessions' | 'contacts'
const STORES: StoreName[] = ['keys', 'sessions', 'contacts']

export interface IdbStore {
  get<T>(store: StoreName, key: string): Promise<T | undefined>
  getAll<T>(store: StoreName): Promise<T[]>
  put(store: StoreName, value: unknown, key: string): Promise<void>
  delete(store: StoreName, key: string): Promise<void>
  clear(store: StoreName): Promise<void>
}

let dbPromise: Promise<IDBDatabase> | null = null

function rawOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      for (const name of STORES) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'))
  })
}

/** Open (once, memoized) the convariance database. */
export function openDb(): Promise<IdbStore> {
  if (!dbPromise) dbPromise = rawOpen()

  async function tx<T>(
    store: StoreName,
    mode: IDBTransactionMode,
    run: (s: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await dbPromise!
    return new Promise<T>((resolve, reject) => {
      const t = db.transaction(store, mode)
      const req = run(t.objectStore(store))
      t.onerror = () =>
        reject(t.error ?? req.error ?? new Error('IndexedDB transaction failed'))
      t.oncomplete = () => resolve(req.result)
    })
  }

  const api: IdbStore = {
    get: (store, key) => tx(store, 'readonly', (s) => s.get(key)),
    getAll: (store) => tx(store, 'readonly', (s) => s.getAll()),
    put: (store, value, key) =>
      tx(store, 'readwrite', (s) => s.put(value, key)).then(() => undefined),
    delete: (store, key) =>
      tx(store, 'readwrite', (s) => s.delete(key)).then(() => undefined),
    clear: (store) =>
      tx(store, 'readwrite', (s) => s.clear()).then(() => undefined)
  }
  return Promise.resolve(api)
}

/** Wipe every store. Backs the "clear all keys and local data" path (006 F8). */
export async function wipeAll(): Promise<void> {
  const db = await openDb()
  await Promise.all(STORES.map((s) => db.clear(s)))
}
